import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { stripHtml } from 'string-strip-html';
import { FormHelperText, Typography } from '@material-ui/core';
import ArrowTooltip from '../arrowTooltip';
import ActionButton from '../actionButton';
import InputField from '../inputField';
import {
  replaceObjectInList,
  asyncDebounce,
  isObjectEmpty,
} from '../../../utility/helpers';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import {
  ANSWER_TITLE_ERROR,
  QUESTION_TITLE_ERROR,
  TEXT,
  NEUTRAL_ANSWER,
  N_A,
  WARNING_BREAKPOINT_ANSWERS,
  WARNING_BREAKPOINTS_CHARACTERS,
  BOX_SIZE_BREAKPOINT,
  DELAY,
  getQuestionActionItems,
} from './config';
import CustomRichTextEditor from '../customRichTextEditor';
import RichTextPreview from '../richTextPreview';
import DotsMenu from '../dotsMenu';

const styles = ({ palette: { primary }, spacing }) => ({
  questionWrapper: {
    marginBottom: spacing(6),
  },
  headerSection: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
  },
  menuButton: {
    padding: spacing(1, 2),
  },
  copyButton: {
    marginRight: spacing(2),
  },
  deleteButton: {
    paddingRight: 0,
  },
  answersWrapper: {
    padding: spacing(6, 0, 0, 9),
  },
  horizontalAnswerWeight: {
    justifyContent: 'center',
    display: 'flex',
  },
  verticalAnswerWeight: {
    fontSize: 12,
    fontFamily: 'ProximaNova-Regular',
  },
  label: {
    lineHeight: '24px',
  },
  weightLabelWrapper: {
    justifyContent: 'end',
    marginBottom: spacing(-3.25),
    marginTop: spacing(3.25),
  },
  verticalAnswerWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(2),
    justifyContent: 'space-between',
    '& .MuiFormControl-root': {
      width: '91.5%',
    },
  },
  focusedVerticalAnswerWrapper: {
    marginBottom: spacing(10),
  },
  verticalAnswerRichTextWrapper: {
    marginBottom: spacing(2),
  },
  weightBlock: {
    display: 'flex',
  },
  horizontalWeightLabel: {
    marginTop: spacing(18.5),
  },
  horizontalAnswers: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  horizontalAnswerWrapper: {
    marginRight: spacing(2),
    display: 'grid',
    justifyContent: 'flex-start',
    '&:last-child': {
      marginRight: 0,
    },
  },
  horizontalMediumBox: {
    width: 78.5,
  },
  horizontalLargeBox: {
    width: 107,
  },
  horizontalSmallBox: {
    width: 'auto',
  },
  inputStyle: {
    backgroundColor: primary.white,
    marginBottom: spacing(2),
  },
  inputTitleStyle: {
    marginBottom: 0,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 18,
    lineHeight: '22px',
  },
  inputHorizontalStyle: {
    padding: spacing(0, 0.5),
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: spacing(1),
  },
  tooltip: {
    fontFamily: 'ProximaNova-Regular',
    fontSize: '14px',
    lineHeight: '20px',
    color: primary.bluish2,
    margin: '7px 8px',
  },
  titleErrorMessage: {
    position: 'absolute',
    bottom: -20,
  },
  fieldWithError: {
    marginBottom: 0,
  },
  inputHorizontalAnswers: {
    width: '100%',
    textAlign: 'center',
  },
  dragButton: {
    marginRight: spacing(1.5),
  },
  customError: {
    top: 148,
  },
  mainRichText: {
    width: '100%',
  },
  richTextEditorWrapper: {
    width: '91.5%',
  },
  textEditor: {
    height: 170,
    marginBottom: spacing(0.5),
    '& .ql-container': {
      height: 150,
    },
  },
  richTextBorder: {
    border: `1px solid ${primary.bluish6}`,
    padding: spacing(4),
    boxSizing: 'border-box',
    borderRadius: spacing(2),
    width: '91.5%',
  },
  richTextBorderWithError: {
    marginBottom: spacing(3),
  },
  richTextEditorWithError: {
    top: 198,
    paddingLeft: spacing(0.5),
  },
  errorMessage: {
    position: 'relative',
    left: -15,
    bottom: -37,
  },
  richTextPreview: {
    border: `1px solid ${primary.bluish6}`,
    backgroundColor: primary.bluish9,
  },
  richTextPreviewText: {
    color: primary.bluish5,
  },
  collapseButton: {
    alignSelf: 'flex-start',
    marginLeft: spacing(2),
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
    width: 12,
    height: 12,
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  dotsMenu: {
    transform: 'rotate(90deg)',
  },
  disabledLabel: {
    opacity: '30%',
  },
});

class QuestionEntry extends PureComponent {
  constructor(props) {
    super(props);
    this.handleInputChange = asyncDebounce(this.handleInputChange, DELAY);
  }

  state = {
    answers: [],
    titleText: '',
    trackInitialValueLength: '',
    isMaxReached: false,
    isRichTextEditor: [],
    isExpanded: true,
  };

  componentDidMount() {
    const { isVertical, item, switchHorizontalWarning } = this.props;
    const { answers, text } = item;

    if (!isVertical) {
      const isMaxReached = answers.some(answer => {
        return this.isMaxNumberOfCharactersReached(answer.text, answers.length);
      });
      if (isMaxReached) {
        this.setIsMaxReached(true);
        switchHorizontalWarning(item.unique_id);
      }
    }
    this.setState({
      answers,
      titleText: text,
      isRichTextEditor: Array(answers.length).fill(false),
    });
  }

  componentDidUpdate(prevProps) {
    const { answers, isMaxReached } = this.state;
    const { switchHorizontalWarning, isVertical, item } = this.props;

    if (prevProps.item.answers.length !== item.answers.length) {
      this.setState({
        answers: item.answers,
        titleText: item.text,
        isRichTextEditor: Array(item.answers.length).fill(false),
      });
    }
    if (prevProps.isVertical !== isVertical && !isVertical) {
      const checkIsMaxReached = answers.some(answer => {
        return this.isMaxNumberOfCharactersReached(answer.text, answers.length);
      });
      if (isMaxReached !== checkIsMaxReached) {
        switchHorizontalWarning(item.unique_id);
        this.setIsMaxReached(checkIsMaxReached);
      }
    }
  }

  componentWillUnmount() {
    const { switchHorizontalWarning, item } = this.props;
    const { isMaxReached } = this.state;
    if (isMaxReached) {
      switchHorizontalWarning(item.unique_id);
    }
  }

  setIsMaxReached = isMaxReached => {
    this.setState({ isMaxReached });
  };

  isMaxNumberOfCharactersReached = (answer, numberOfAnswers) => {
    const { hasNeutralAnswer } = this.props;
    let reducedNumberOfAnswers = numberOfAnswers;
    if (hasNeutralAnswer) {
      reducedNumberOfAnswers -= 1;
      if (answer === NEUTRAL_ANSWER || answer === N_A) {
        return false;
      }
    }
    const numberOfCharacters = answer.length;
    return (
      (reducedNumberOfAnswers <= WARNING_BREAKPOINT_ANSWERS &&
        numberOfCharacters > WARNING_BREAKPOINTS_CHARACTERS.MEDIUM) ||
      (reducedNumberOfAnswers > WARNING_BREAKPOINT_ANSWERS &&
        numberOfCharacters > WARNING_BREAKPOINTS_CHARACTERS.SMALL)
    );
  };

  tooltipText = () => {
    const { classes, translations, disableActions } = this.props;
    return (
      <div className={classes.tooltip}>
        {disableActions
          ? translations.draggableTooltipDisabled
          : translations.draggableTooltip}
      </div>
    );
  };

  handleInputChange = async () => {
    const { onSave, item } = this.props;

    const preparedData = {
      ...item,
      answers: this.state.answers,
      text: this.state.titleText,
    };
    await onSave(preparedData);
  };

  handleTitleChange = e => {
    const { value } = e.target;
    this.setState({
      titleText: value,
    });
    this.handleInputChange();
  };

  handleAnswerChange = (value, fieldName, index) => {
    const { isVertical, item, switchHorizontalWarning } = this.props;
    const { answers, isMaxReached, trackInitialValueLength } = this.state;

    if (!isVertical) {
      const isWarningConditionReached = this.isMaxNumberOfCharactersReached(
        value,
        answers.length
      );
      const initialWarningCondition = this.isMaxNumberOfCharactersReached(
        trackInitialValueLength,
        answers.length
      );

      if (initialWarningCondition !== isWarningConditionReached) {
        if (isWarningConditionReached) {
          if (isMaxReached !== isWarningConditionReached) {
            this.setState(
              {
                isMaxReached: isWarningConditionReached,
                trackInitialValueLength: value,
              },
              () => switchHorizontalWarning(item.unique_id)
            );
          }
        } else {
          const updateMaxReached = answers.some((answer, i) => {
            if (i === index && answer.text !== value) {
              return this.isMaxNumberOfCharactersReached(value, answers.length);
            }
            return this.isMaxNumberOfCharactersReached(
              answer.text,
              answers.length
            );
          });

          if (isMaxReached !== updateMaxReached) {
            this.setState(
              {
                isMaxReached: updateMaxReached,
                trackInitialValueLength: value,
              },
              () => switchHorizontalWarning(item.unique_id)
            );
          }
        }
      }
    }

    this.setState({
      answers: replaceObjectInList(answers, index, {
        [TEXT]: value,
      }),
    });
    this.handleInputChange();
  };

  handleExpandQuestion = () => {
    const { isExpanded } = this.state;

    this.setState({ isExpanded: !isExpanded });
  };

  render() {
    const {
      id,
      classes,
      item,
      hasErrors,
      onArchive,
      onDuplicate,
      onDelete,
      translations,
      isOpenEndedAttribute,
      hasDefinedAnswers,
      dragRef,
      dragProps,
      isVertical,
      isEditDisabled,
      answerWeights,
    } = this.props;

    const { answers, titleText, isRichTextEditor, isExpanded } = this.state;

    const titleHasError = !!hasErrors?.[QUESTION_TITLE_ERROR];
    const numberOfAnswers = item.answers.length;
    const isDisabledQuestion = isEditDisabled && item.is_used_in_survey;

    return (
      <div
        ref={dragRef}
        id={id}
        {...(dragProps ? { ...dragProps.draggableProps } : {})}
        className={classes.questionWrapper}
      >
        <Grid container spacing={0} className={classes.headerSection}>
          <Grid item xs={11} className={classes.leftSection}>
            <ArrowTooltip tooltipLabel={this.tooltipText()} position="top">
              <ActionButton
                className={classes.dragButton}
                type={ACTION_BUTTON_TYPES.DRAG}
                {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
              />
            </ArrowTooltip>
            <InputField
              className={classes.inputTitleStyle}
              customErrorClass={classes.titleErrorMessage}
              value={titleText}
              placeholder={translations.placeholders.questionPlaceholder}
              error={titleHasError}
              errorMessage={translations[hasErrors?.[QUESTION_TITLE_ERROR]]}
              disabled={isDisabledQuestion}
              onClick={e => e.stopPropagation()}
              onChange={this.handleTitleChange}
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={1} className={classes.actionButtons}>
            {!isOpenEndedAttribute && (
              <ActionButton
                className={classes.collapseButton}
                iconClass={classNames(classes.expandIcon, {
                  [classes.expandIconOpened]: isExpanded,
                })}
                size="small"
                edge="end"
                type={ACTION_BUTTON_TYPES.ARROW}
                onClickHandler={this.handleExpandQuestion}
              />
            )}
            <DotsMenu
              iconButtonClass={classes.dotsMenu}
              menuItems={getQuestionActionItems(
                translations.questionActions,
                item,
                onDuplicate,
                onDelete,
                onArchive,
                !isObjectEmpty(hasErrors)
              )}
            />
          </Grid>
        </Grid>
        {isExpanded && (
          <>
            {hasDefinedAnswers && isVertical && (
              <div
                className={classNames(
                  classes.headerSection,
                  classes.weightLabelWrapper
                )}
              >
                <Typography
                  variant="caption"
                  className={classNames(classes.label, {
                    [classes.disabledLabel]: isDisabledQuestion,
                  })}
                >
                  {translations.weight}
                </Typography>
              </div>
            )}
            {hasDefinedAnswers && (
              <div className={classes.weightBlock}>
                <Grid
                  item
                  xs={12}
                  className={classNames(classes.answersWrapper, {
                    [classes.horizontalAnswers]: !isVertical,
                  })}
                >
                  {answers.map((answer, index) => {
                    const fieldName = `${ANSWER_TITLE_ERROR}_${index}`;
                    const answerHasError = !!hasErrors?.[fieldName];
                    const answerInputClasses = classNames(classes.inputStyle, {
                      [classes.inputHorizontalStyle]: !isVertical,
                      [classes.fieldWithError]: answerHasError,
                    });
                    const isNeutralAnswer = +answerWeights[index] < 0;
                    const inputValue = isNeutralAnswer ? N_A : answer.text;
                    const weightValue = isNeutralAnswer
                      ? ''
                      : `${answerWeights[index]}`;
                    const isFocused = isRichTextEditor[index];
                    const errorMessage = translations[hasErrors?.[fieldName]];
                    const modules = {
                      toolbar: [['bold', 'italic', { list: 'bullet' }]],
                    };

                    return (
                      <div
                        key={`answer_${index}`}
                        className={classNames({
                          [classes.horizontalAnswerWrapper]: !isVertical,
                          [classes.verticalAnswerWrapper]:
                            isVertical && !isNeutralAnswer,
                          [classes.focusedVerticalAnswerWrapper]:
                            isVertical && !isNeutralAnswer && isFocused,
                          [classes.verticalAnswerRichTextWrapper]:
                            isVertical &&
                            (!isFocused || isDisabledQuestion) &&
                            !answerHasError,
                          [classes.horizontalMediumBox]:
                            numberOfAnswers <
                              BOX_SIZE_BREAKPOINT.THRESHOLD_FOR_LARGE &&
                            !isVertical,
                          [classes.horizontalLargeBox]:
                            numberOfAnswers <
                              BOX_SIZE_BREAKPOINT.THRESHOLD_FOR_MEDIUM &&
                            !isVertical,
                          [classes.horizontalSmallBox]:
                            numberOfAnswers >=
                              BOX_SIZE_BREAKPOINT.THRESHOLD_FOR_LARGE &&
                            !isVertical,
                        })}
                      >
                        {isFocused &&
                          !isNeutralAnswer &&
                          isVertical &&
                          !isDisabledQuestion && (
                            <div
                              className={classes.richTextEditorWrapper}
                              tabIndex={0}
                            >
                              <CustomRichTextEditor
                                customEditorClass={classes.textEditor}
                                placeholder={translations.placeholders.answer}
                                customErrorClass={classNames({
                                  [classes.richTextEditorWithError]:
                                    answerHasError,
                                })}
                                modules={modules}
                                className={classes.mainRichText}
                                isReadOnly={isNeutralAnswer}
                                value={inputValue || ''}
                                hasError={answerHasError}
                                errorMessage={errorMessage}
                                onUpdateText={value =>
                                  this.handleAnswerChange(
                                    value,
                                    fieldName,
                                    index
                                  )
                                }
                              />
                            </div>
                          )}
                        {(!isFocused ||
                          isNeutralAnswer ||
                          isDisabledQuestion) &&
                          isVertical && (
                            <div
                              className={classNames(
                                {
                                  [classes.richTextBorderWithError]:
                                    answerHasError,
                                  [classes.richTextPreview]: isNeutralAnswer,
                                },
                                classes.richTextBorder
                              )}
                              onClick={() => {
                                this.setState({
                                  trackInitialValueLength: inputValue,
                                  ...(isNeutralAnswer
                                    ? {}
                                    : {
                                        isRichTextEditor: isRichTextEditor.map(
                                          (val, i) => index === i
                                        ),
                                      }),
                                });
                              }}
                            >
                              <RichTextPreview
                                className={classNames({
                                  [classes.richTextPreviewText]:
                                    isNeutralAnswer,
                                })}
                                text={inputValue}
                              />
                              {answerHasError && errorMessage && (
                                <FormHelperText
                                  className={classes.errorMessage}
                                  error={answerHasError}
                                >
                                  {errorMessage}
                                </FormHelperText>
                              )}
                            </div>
                          )}
                        {!isVertical && (
                          <InputField
                            className={answerInputClasses}
                            customInputClass={classes.inputHorizontalAnswers}
                            disabled={isNeutralAnswer || isDisabledQuestion}
                            placeholder={translations.placeholders.answer}
                            value={stripHtml(inputValue).result}
                            error={answerHasError}
                            errorMessage={errorMessage}
                            onClick={e => e.stopPropagation()}
                            onChange={e =>
                              this.handleAnswerChange(
                                e.target.value,
                                fieldName,
                                index
                              )
                            }
                            onInputFocus={value =>
                              this.setState({
                                trackInitialValueLength: value,
                              })
                            }
                          />
                        )}
                        <Typography
                          variant="subtitle1"
                          className={classNames(classes.label, {
                            [classes.horizontalAnswerWeight]: !isVertical,
                            [classes.disabledLabel]: isDisabledQuestion,
                          })}
                        >
                          {weightValue}
                        </Typography>
                      </div>
                    );
                  })}
                </Grid>
                {!isVertical && (
                  <Typography
                    variant="caption"
                    className={classNames(
                      classes.label,
                      classes.horizontalWeightLabel,
                      { [classes.disabledLabel]: isDisabledQuestion }
                    )}
                  >
                    {translations.weight}
                  </Typography>
                )}
              </div>
            )}{' '}
          </>
        )}
      </div>
    );
  }
}

QuestionEntry.defaultProps = {
  id: undefined,
  disableActions: false,
  dragRef: () => {},
  dragProps: null,
  hasDefinedAnswers: false,
  hasErrors: {},
};

QuestionEntry.propTypes = {
  classes: PropTypes.object.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  item: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  disableActions: PropTypes.bool,
  isVertical: PropTypes.bool.isRequired,
  hasDefinedAnswers: PropTypes.bool,
  hasNeutralAnswer: PropTypes.bool.isRequired,
  dragRef: PropTypes.func,
  dragProps: PropTypes.object,
  switchHorizontalWarning: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  answerWeights: PropTypes.array.isRequired,
  hasErrors: PropTypes.object,
  isEditDisabled: PropTypes.bool.isRequired,
  isOpenEndedAttribute: PropTypes.bool.isRequired,
};

export default withStyles(styles)(QuestionEntry);
